import { memoize } from "@use/decorators";

import { ElementModel, ElementModelData } from "@courses-pm2/api/src/ts/models/ElementModel";
import { parseSimpleHtml } from "../../parser/parseSimpleHtml";

/**
 * Specification of data needed to instantiate a new ElementLinkModel.
 */
type CallToActionCardsData = {
    [K in "title" | "cards"]: CallToActionCardsModel[K];
}

export type CtaComponentCard = {
    title: string;
    description: string | undefined;
    imageSrc: string | undefined;
    link: {
      id: string;
      target: "lightbox" | "download" | "new-tab" | "same-tab";
      href: string;
    };

    meta: {
      colorVariant: "type-1" | "type-2" | "type-3" | undefined;
    };
  };


/**
 * Describes the content to be rendered for the element, so that it's made
 * easily available.
 */
export class CallToActionCardsModel extends ElementModel {
    public readonly title: string | undefined;
    public readonly cards: Array<CtaComponentCard>;


    /**
     * Apply data given when a new model is being instantiated.
     */
    constructor(initial: CallToActionCardsData & ElementModelData) {
        super(initial);

        this.title = initial.title;
        this.cards = initial.cards;
    }

    /**
     * Preview is unavailable for CallToActionCards.
     */
    public async getPreviewImageUrl(): Promise<undefined> {
        return undefined;
    }

    /**
     * Preview is unavailable for CallToActionCards.
     */
    public getPreviewText(): string | undefined {
        return undefined;
    }

    /**
     * @inheritdoc
     *
     * Element links are able to contain RichTextNodes, so use the helper from
     * the RichtextModel to calculate standard pages for the content.
     */
    // tslint:disable-next-line: completed-docs
    @memoize()
    public getStandardPageCount(): number {
        return 0;
    }

    /**
     * Helper that'll render the caption of the given slide, adding support for
     * certain simple html tags.
     */
    @memoize()
    public renderCardDescription(index: number): React.ReactNode {
        const card = this.cards[index];

        return (
            card && card.description
                ? parseSimpleHtml(card.description)
                : undefined
        );
    }
}
